import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNotificationContext } from '@context/NotificationsContext';
import { useDesktop } from '@utils/hooks/useDesktop';
import { NOTIFICATION_NAMES_MAP } from '@utils/constants';
import { useCommerceFromStore } from '@store/actions/commerce';
import type { PlickNotification } from '@interfaces/general';

const LegacyCard = ({
  title,
  subtitle,
  description,
  body,
  icon,
  accentColor,
  onAccept,
  onReject,
  acceptLabel,
  rejectLabel,
  autoCloseTimer,
}: PlickNotification) => {
  const isDesktop = useDesktop();
  const timeOutBar = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (timeOutBar.current) timeOutBar.current.style.width = '0%';
    }, 100);
  }, []);

  return (
    <div className="rounded-lg bg-color-white px-3 py-3" style={{ boxShadow: 'rgba(0,0,0,0.25) 0px 0px 10px 5px' }}>
      <div className={`flex ${isDesktop ? 'flex-row' : 'flex-col'}`}>
        <div className="flex flex-1 items-center">
          <div
            className="w-[41px] min-w-[41px] h-10 rounded-[50%] flex items-center justify-center"
            style={{ backgroundColor: accentColor }}
          >
            <Icon icon={icon} color="#ffffff" width="28px" height="28px" />
          </div>
          <div className="ml-3">
            <p className="text-lg font-semibold">
              {title} {subtitle ? <b>{subtitle}</b> : null}
            </p>
            <p>{description}</p>
            {body ?? null}
          </div>
        </div>
        {(onAccept || onReject) && (
          <div
            className="flex flex-col justify-center"
            style={{
              borderLeft: isDesktop ? '1px solid #C0C0C0' : 'none',
              width: isDesktop ? '135px' : '100%',
              marginTop: isDesktop ? '0px' : '12px',
              paddingLeft: isDesktop ? '12px' : '0px',
            }}
          >
            {onAccept && (
              <button
                className="font-bold text-color-white py-2 rounded-md flex items-center justify-center mb-1"
                style={{ backgroundColor: accentColor }}
                onClick={() => onAccept()}
              >
                {acceptLabel ? acceptLabel.toUpperCase() : 'ACEPTAR'}
                <Icon className="ml-1" icon="mingcute:check-fill" color="#ffffff" />
              </button>
            )}
            {onReject && (
              <button
                className="font-bold py-2 flex items-center justify-center"
                onClick={() => onReject()}
                style={{ color: '#FF3939', marginTop: isDesktop ? '0' : '4px' }}
              >
                {rejectLabel ? rejectLabel.toUpperCase() : 'RECHAZAR'}
                <Icon className="ml-1" icon="ep:close-bold" color="#FF3939" />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        ref={autoCloseTimer ? timeOutBar : null}
        className="h-2 rounded-md"
        style={{
          marginTop: '8px',
          backgroundColor: accentColor,
          width: '100%',
          transition: autoCloseTimer ? `width ${autoCloseTimer}ms ease-in-out` : 'none',
        }}
      />
    </div>
  );
};

const QUICK_PICKS = [15, 20, 25, 30, 45, 60] as const;

const DEFAULT_PREP_TIME = 25;

export const NotificationCenterV2 = () => {
  const isDesktop = useDesktop();
  const { notifications } = useNotificationContext();
  const commerceStore = useCommerceFromStore();
  const defaultCookTime = commerceStore?.default_cook_time ?? null;
  const prepEnabled = Boolean(defaultCookTime && defaultCookTime > 0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const timerBarRef = useRef<HTMLDivElement>(null);

  const [prepTime, setPrepTime] = useState(() =>
    defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_PREP_TIME,
  );
  const [prepEditing, setPrepEditing] = useState(false);

  const incomingOrders = notifications.filter((n) => n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key));
  const otherNotifications = notifications.filter((n) => !n.id.endsWith(NOTIFICATION_NAMES_MAP.INCOMING_ORDER.key));

  useEffect(() => {
    if (incomingOrders.length === 0) {
      setCurrentIndex(0);
    } else if (currentIndex >= incomingOrders.length) {
      setCurrentIndex(incomingOrders.length - 1);
    }
  }, [incomingOrders.length, currentIndex]);

  const current = incomingOrders[currentIndex];

  useEffect(() => {
    setPrepTime(defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_PREP_TIME);
    setPrepEditing(false);
  }, [current?.id, defaultCookTime]);

  useEffect(() => {
    if (!timerBarRef.current) return;
    timerBarRef.current.style.transition = 'none';
    timerBarRef.current.style.width = '100%';
    const id = setTimeout(() => {
      if (!timerBarRef.current) return;
      const duration = current?.autoCloseTimer ?? 30000;
      timerBarRef.current.style.transition = `width ${duration}ms linear`;
      timerBarRef.current.style.width = '0%';
    }, 100);
    return () => clearTimeout(id);
  }, [current?.id]);

  const total = incomingOrders.length;

  const commitPrepEdit = useCallback(() => {
    if (!prepTime) setPrepTime(defaultCookTime && defaultCookTime > 0 ? defaultCookTime : DEFAULT_PREP_TIME);
    setPrepEditing(false);
  }, [prepTime, defaultCookTime]);

  if (notifications.length === 0) return null;

  return (
    <div
      className={`fixed bottom-0 right-0 z-[99] flex flex-col gap-3 ${
        isDesktop ? 'w-[400px] mr-5 mb-5' : 'w-full px-3 pb-[70px]'
      }`}
      style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
    >
      {/* Legacy notifications (RIDER_ARRIVED, ORDER_DELIVERED, etc.) */}
      {otherNotifications.map((n) => (
        <LegacyCard key={n.id} {...n} />
      ))}

      {/* Incoming-order widget */}
      {current && (
        <div
          className="rounded-[12px] overflow-hidden"
          style={{
            boxShadow: '0 16px 40px rgba(37,55,70,.22)',
            animation: 'plick-toast-in 280ms cubic-bezier(.2,.8,.2,1)',
          }}
        >
          {/* Red header bar — only when multiple orders are queued */}
          {total > 1 && (
            <div
              className="flex items-center justify-between px-3 py-[5px] text-white"
              style={{ backgroundColor: '#ff3939' }}
            >
              <span className="font-bold uppercase tracking-[0.04em] flex items-center gap-2" style={{ fontSize: 11 }}>
                <span
                  className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0"
                  style={{ animation: 'plick-nc-pulse 1.4s ease-out infinite' }}
                />
                Pedido {currentIndex + 1} de {total} nuevos
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                  className="w-5 h-5 flex items-center justify-center rounded disabled:opacity-40"
                  style={{ backgroundColor: currentIndex > 0 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)' }}
                  aria-label="Anterior pedido"
                >
                  <Icon icon="bxs:chevron-left" width={14} color="#ffffff" />
                </button>
                <button
                  onClick={() => setCurrentIndex((i) => Math.min(total - 1, i + 1))}
                  disabled={currentIndex === total - 1}
                  className="w-5 h-5 flex items-center justify-center rounded disabled:opacity-40"
                  style={{
                    backgroundColor: currentIndex < total - 1 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)',
                  }}
                  aria-label="Siguiente pedido"
                >
                  <Icon icon="bxs:chevron-right" width={14} color="#ffffff" />
                </button>
              </div>
            </div>
          )}

          {/* Order summary */}
          <div className="flex items-center gap-3" style={{ padding: '12px 14px 10px', backgroundColor: '#ffffff' }}>
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(87,189,227,0.2)' }}
            >
              <Icon icon={current.icon} color={current.accentColor} width={28} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-tight" style={{ fontSize: 13, color: '#253746' }}>
                {current.title} <strong>{current.subtitle ?? ''}</strong>
              </p>
              <p style={{ fontSize: 12, color: '#6B6B6B', marginTop: 2 }}>{current.description}</p>
              {current.body ? <div style={{ fontSize: 12, marginTop: 2 }}>{current.body}</div> : null}
            </div>
          </div>

          {/* Prep-time editor — only for pickup/dine-in; not for home delivery */}
          {prepEnabled && current.metodo_entrega !== 'delivery' && (
            <div
              style={{
                padding: '10px 14px 12px',
                background: '#F8FBFC',
                borderTop: '1px solid #E8EEF2',
                borderBottom: '1px solid #E8EEF2',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ color: '#4298B5', flexShrink: 0 }}
                  >
                    <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 9v4l2.5 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M9 2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span style={{ fontSize: 11.5, color: '#253746', fontWeight: 600 }}>Listo en</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {prepEditing ? (
                    <input
                      type="number"
                      autoFocus
                      min={1}
                      max={240}
                      value={prepTime}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        setPrepTime(Number.isFinite(n) ? Math.max(1, Math.min(240, n)) : 0);
                      }}
                      onBlur={commitPrepEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitPrepEdit();
                      }}
                      style={{
                        width: 64,
                        fontSize: 15,
                        fontWeight: 700,
                        textAlign: 'right',
                        color: '#253746',
                        border: '1.5px solid #4298B5',
                        borderRadius: 6,
                        padding: '4px 8px',
                        outline: 0,
                        fontFamily: 'inherit',
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => setPrepEditing(true)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: '#ffffff',
                        border: '1.5px solid #E8EEF2',
                        borderRadius: 6,
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        color: '#253746',
                      }}
                    >
                      <span style={{ fontSize: 15, fontWeight: 700 }}>{prepTime}</span>
                      <span style={{ fontSize: 11, color: '#6B6B6B', fontWeight: 600 }}>min</span>
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        style={{ color: '#6B6B6B', marginLeft: 2 }}
                      >
                        <path
                          d="M11 4H4v16h16v-7M18.5 2.5a2.12 2.12 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Quick picks */}
              <div className="flex flex-wrap gap-1" style={{ marginTop: 8 }}>
                {QUICK_PICKS.map((p) => {
                  const active = prepTime === p;
                  return (
                    <button
                      key={p}
                      onClick={() => {
                        setPrepTime(p);
                        setPrepEditing(false);
                      }}
                      style={{
                        background: active ? '#4298B5' : '#ffffff',
                        color: active ? '#ffffff' : '#253746',
                        border: `1px solid ${active ? '#4298B5' : '#E8EEF2'}`,
                        borderRadius: 9999,
                        padding: '3px 9px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 10.5,
                        fontWeight: 600,
                        transition: 'all 120ms',
                      }}
                    >
                      {p} min
                    </button>
                  );
                })}
              </div>

              {/* Helper */}
              <div
                className="flex items-start gap-1.5"
                style={{ marginTop: 8, fontSize: 10.5, color: '#6B6B6B', lineHeight: 1.45 }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ color: '#4298B5', flexShrink: 0, marginTop: 1 }}
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                <span>Tu cliente recibirá una notificación con este tiempo de preparación.</span>
              </div>
            </div>
          )}

          {/* Actions row */}
          <div className="flex items-center gap-2" style={{ padding: '10px 14px', backgroundColor: '#ffffff' }}>
            {current.onReject && (
              <button
                onClick={current.onReject}
                className="flex items-center gap-1 font-bold uppercase"
                style={{
                  background: 'transparent',
                  border: 0,
                  color: '#FF3939',
                  fontSize: 11,
                  letterSpacing: '.03em',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  fontFamily: 'inherit',
                }}
              >
                {(current.rejectLabel ?? 'Ignorar').toUpperCase()}
                <Icon icon="ep:close-bold" color="#FF3939" width={12} />
              </button>
            )}
            {current.onAccept && (
              <button
                onClick={current.onAccept}
                className="flex items-center justify-center gap-1.5 rounded-md font-bold"
                style={{
                  marginLeft: 'auto',
                  backgroundColor: '#1fc49a',
                  color: '#ffffff',
                  fontSize: 13,
                  padding: '8px 16px',
                  border: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {prepEnabled && current.metodo_entrega !== 'delivery'
                  ? `Aceptar · ${prepTime || DEFAULT_PREP_TIME} min`
                  : (current.acceptLabel ?? 'Aceptar')}
                <Icon icon="mingcute:check-fill" width={15} color="#ffffff" />
              </button>
            )}
          </div>

          {/* Timer bar */}
          <div style={{ height: 4, background: '#E8EEF2', position: 'relative', overflow: 'hidden' }}>
            <div ref={timerBarRef} style={{ height: '100%', backgroundColor: '#4298B5', width: '100%' }} />
          </div>
        </div>
      )}
      <style>{`
        @keyframes plick-toast-in {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @keyframes plick-nc-pulse {
          0%  { transform: scale(.85); opacity: .6; }
          70% { transform: scale(1.4); opacity: 0; }
          100%{ transform: scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
};
